import { useCallback, useMemo, useState, useEffect } from "react";
import { Users, LogOut, Award, CheckCircle2, Eye } from "lucide-react";
import dayjs from "dayjs";
import { Select, DatePicker } from "antd";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Textarea } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Reactable } from "../../components/ui/reactable";
import { DialogModal } from "../../components/ui/dialogModal";
import { SegmentSwitch } from "../../components/ui/segmentSwitch";
import { antPickerClass, antSelectClass, modalTextareaClass } from "../../components/ui/fieldStyles";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { getPrintData } from "../../components/common/CommonFunctions";
import { useInternshipPermissions } from "../../hooks/useInternshipPermissions";
import {
    INTERNSHIP_ENROLMENTS,
    INTERNSHIP_PROGRESS,
    INTERNSHIP_CERTIFICATE,
    INTERNSHIP_PROGRAMS,
    EMPLOYEE_PERSONAL_INFO,
} from "../../constants/api";
import {
    EnrolmentStatusPill,
    PhaseStatusPill,
    ModalField,
    employeeOption,
    enrolmentStatusLabels,
    ProgramPreview,
    programTrainingCount,
} from "./internshipUi";

const STATUS_TABS = [
    { value: "", label: "All" },
    { value: "active", label: "Active" },
    { value: "completed", label: "Completed" },
    { value: "withdrawn", label: "Withdrawn" },
];

const SELECT_CLASS = antSelectClass();
const PICKER_CLASS = antPickerClass();

const InternshipEnrolments = () => {
    const { fetch, create, update } = useApiMethods();
    const { canManageEnrolments } = useInternshipPermissions();

    const [enrolments, setEnrolments] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [statusFilter, setStatusFilter] = useState("");

    const [enrolForm, setEnrolForm] = useState(null);
    const [withdrawTarget, setWithdrawTarget] = useState(null);
    const [withdrawReason, setWithdrawReason] = useState("");
    const [signOff, setSignOff] = useState(null);
    const [detail, setDetail] = useState(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const filters = statusFilter ? { status: statusFilter } : {};
            const res = await fetch(INTERNSHIP_ENROLMENTS, filters);
            setEnrolments(res?.data || []);
        } catch (_) {
            ToastMsg("error", "Failed to load enrolments");
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    const loadLookups = useCallback(async () => {
        try {
            const [prog, emp] = await Promise.all([
                fetch(INTERNSHIP_PROGRAMS),
                fetch(EMPLOYEE_PERSONAL_INFO, { drop_down: "True" }),
            ]);
            setPrograms(prog?.data || []);
            setEmployees((emp?.data || []).filter((e) => e.is_active).map(employeeOption));
        } catch (_) { /* lookups are non-fatal */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { loadData(); }, [loadData]);
    useEffect(() => { loadLookups(); }, [loadLookups]);

    const programOptions = useMemo(
        () => programs.map((p) => ({ value: p.id, label: p.name })),
        [programs],
    );

    const selectedProgram = useMemo(
        () => programs.find((p) => p.id === enrolForm?.program_id) || null,
        [programs, enrolForm?.program_id],
    );

    const expectedEndFromProgram = (program, start) => {
        if (!program?.duration_weeks || !start) return null;
        return start.add(Number(program.duration_weeks), "week");
    };

    const openEnrolForm = () => setEnrolForm({
        program_id: null,
        employee_id: null,
        mentor_id: null,
        start_date: dayjs(),
        expected_end_date: null,
    });

    const alreadyEnrolled = enrolForm?.program_id && enrolForm?.employee_id
        && enrolments.some((e) =>
            e.program_id === enrolForm.program_id
            && e.employee_id === enrolForm.employee_id
            && e.status === "active");

    const saveEnrolment = async () => {
        if (!enrolForm.program_id) {
            ToastMsg("error", "Please select a program");
            return;
        }
        if (!enrolForm.employee_id) {
            ToastMsg("error", "Please select an intern");
            return;
        }
        if (enrolForm.mentor_id && enrolForm.mentor_id === enrolForm.employee_id) {
            ToastMsg("error", "An intern cannot be their own mentor");
            return;
        }
        if (alreadyEnrolled) {
            ToastMsg("error", "This employee is already enrolled on this program");
            return;
        }
        if (selectedProgram && !(selectedProgram.phases || []).length) {
            ToastMsg("error", "Add at least one phase to this program before enrolling");
            return;
        }
        setSaving(true);
        try {
            const res = await create(INTERNSHIP_ENROLMENTS, {
                program_id: enrolForm.program_id,
                employee_id: enrolForm.employee_id,
                mentor_id: enrolForm.mentor_id || null,
                start_date: enrolForm.start_date
                    ? enrolForm.start_date.format("YYYY-MM-DD")
                    : dayjs().format("YYYY-MM-DD"),
                expected_end_date: enrolForm.expected_end_date
                    ? enrolForm.expected_end_date.format("YYYY-MM-DD")
                    : null,
            });
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Intern enrolled");
                setEnrolForm(null);
                await loadData();
            }
        } finally {
            setSaving(false);
        }
    };

    const confirmWithdraw = async () => {
        if (!withdrawReason.trim()) {
            ToastMsg("error", "Please enter a withdrawal reason");
            return;
        }
        setSaving(true);
        try {
            const res = await update(INTERNSHIP_ENROLMENTS, {
                id: withdrawTarget.id,
                action: "withdraw",
                reason: withdrawReason.trim(),
            });
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Enrolment withdrawn");
                setWithdrawTarget(null);
                setWithdrawReason("");
                await loadData();
            }
        } finally {
            setSaving(false);
        }
    };

    const submitSignOff = async () => {
        if (signOff.progress.trainings_outstanding > 0 && !signOff.remarks.trim()) {
            ToastMsg("error", "Please record why you are signing off with outstanding trainings");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                id: signOff.progress.id,
                action: "evaluate",
                rating: signOff.rating || null,
                remarks: signOff.remarks.trim(),
            };
            if (signOff.progress.trainings_outstanding > 0) payload.override = true;
            const res = await update(INTERNSHIP_PROGRESS, payload);
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Phase signed off");
                setSignOff(null);
                await loadData();
            }
        } finally {
            setSaving(false);
        }
    };

    const columns = [
        {
            accessor: "employee_name", Header: "Intern", sortable: true, filterable: true,
            Cell: ({ value }) => <span className="font-medium text-foreground">{value || "-"}</span>,
        },
        {
            accessor: "program_name", Header: "Program", sortable: true, filterable: true,
            filterType: "select",
            filterOptions: programOptions.map((p) => ({ value: p.label, label: p.label })),
            Cell: ({ value }) => value || "-",
        },
        {
            accessor: "mentor_name", Header: "Mentor", sortable: true, filterable: true,
            Cell: ({ value }) => value || "Not assigned",
        },
        {
            accessor: "start_date", Header: "Start Date", sortable: true, filterable: true, filterType: "date",
            Cell: ({ value }) => (value ? dayjs(value).format("DD-MM-YYYY") : "-"),
        },
        {
            accessor: "expected_end_date", Header: "Expected End", sortable: true, filterable: true, filterType: "date",
            Cell: ({ value }) => (value ? dayjs(value).format("DD-MM-YYYY") : "-"),
        },
        {
            accessor: "current_phase", Header: "Current Phase", sortable: true, filterable: true,
            Cell: ({ value }) => value || "-",
        },
        {
            accessor: "progress_percent", Header: "Progress", sortable: true,
            Cell: ({ row }) => {
                const phases = row.phase_progress || [];
                const done = phases.filter((p) => p.status === "completed" || p.status === "evaluated").length;
                const pct = row.progress_percent ?? 0;
                return (
                    <div className="min-w-[110px]">
                        <div className="flex items-center justify-between text-xs mb-1">
                            <span>{pct}%</span>
                            {phases.length ? (
                                <span className="text-muted-foreground">{done}/{phases.length}</span>
                            ) : null}
                        </div>
                        <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                            <div
                                className="h-full rounded-full bg-sidebar-primary"
                                style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
                            />
                        </div>
                    </div>
                );
            },
        },
        {
            accessor: "status", Header: "Status", sortable: true, filterable: true,
            filterType: "select",
            filterOptions: Object.entries(enrolmentStatusLabels).map(([value, label]) => ({ value, label })),
            Cell: ({ value }) => <EnrolmentStatusPill value={value} />,
        },
        {
            accessor: "actions", Header: "Actions",
            Cell: ({ row }) => (
                <div className="flex items-center gap-1">
                    <Button
                        size="sm"
                        variant="ghost"
                        title="View enrolment"
                        onClick={(e) => { e.stopPropagation(); setDetail(row); }}
                    >
                        <Eye className="h-4 w-4" />
                    </Button>
                    {canManageEnrolments && row.status === "active" && (
                        <Button
                            size="sm"
                            variant="ghost"
                            title="Sign off a phase"
                            onClick={(e) => {
                                e.stopPropagation();
                                const next = (row.phase_progress || []).find((p) => p.status !== "evaluated")
                                    || (row.phase_progress || [])[0];
                                if (!next) {
                                    ToastMsg("error", "No phases to sign off");
                                    return;
                                }
                                setSignOff({ enrolment: row, progress: next, rating: null, remarks: "" });
                            }}
                        >
                            <CheckCircle2 className="h-4 w-4" />
                        </Button>
                    )}
                    {row.status === "completed" && (
                        <Button
                            size="sm"
                            variant="ghost"
                            title="Download certificate"
                            onClick={(e) => {
                                e.stopPropagation();
                                getPrintData(INTERNSHIP_CERTIFICATE, { enrolment_id: row.id });
                            }}
                        >
                            <Award className="h-4 w-4" />
                        </Button>
                    )}
                    {canManageEnrolments && row.status === "active" && (
                        <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-600"
                            title="Withdraw intern"
                            onClick={(e) => {
                                e.stopPropagation();
                                setWithdrawTarget(row);
                                setWithdrawReason("");
                            }}
                        >
                            <LogOut className="h-4 w-4" />
                        </Button>
                    )}
                </div>
            ),
        },
    ];

    const signOffPhaseOptions = (signOff?.enrolment?.phase_progress || []).map((p) => ({
        value: p.id,
        label: `${p.sequence}. ${p.phase_name}`,
    }));

    return (
        <ContainerCard scrollable>
            <ModuleTitleCard
                Icon={Users}
                title="Internship Enrolments"
                subtitle="Interns currently working through a program"
                totalRecords={enrolments.length}
                buttonEnable={canManageEnrolments}
                setOpenDialog={openEnrolForm}
                buttonName="Enrol Intern"
            />

            <div className="mb-2 flex flex-wrap items-center gap-3">
                <SegmentSwitch options={STATUS_TABS} value={statusFilter} onChange={setStatusFilter} />
            </div>

            {loading ? (
                <div className="text-center py-10 text-muted-foreground">Loading...</div>
            ) : (
                <Reactable
                    key={`enrolments-${statusFilter}`}
                    pagename="internship-enrolments"
                    data={enrolments}
                    setData={setEnrolments}
                    columns={columns}
                    isCSVRequired
                    isPDFRequired
                    url={INTERNSHIP_ENROLMENTS}
                    printFilter={statusFilter ? { status: statusFilter } : {}}
                    rowSize="sm"
                    tableHeight="calc(100vh - 330px)"
                    onRowClick={(row) => setDetail(row)}
                />
            )}

            <DialogModal
                open={!!enrolForm}
                onClose={() => setEnrolForm(null)}
                title="Enrol Intern"
                onSave={saveEnrolment}
                saveLabel="Enrol"
                closeLabel="Cancel"
                loading={saving}
                width="600px"
            >
                {enrolForm && (
                    <form className="space-y-4">
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Assigning an intern creates every training listed on the program phases, unless they already hold it or training is off on their employee profile.
                        </p>
                        <ModalField label="Program" htmlFor="enrol-program" required>
                            <Select
                                id="enrol-program"
                                className={SELECT_CLASS}
                                size="large"
                                placeholder="Select internship program"
                                showSearch
                                optionFilterProp="label"
                                options={programOptions}
                                value={enrolForm.program_id || undefined}
                                onChange={(v) => {
                                    const next = programs.find((p) => p.id === v);
                                    setEnrolForm({
                                        ...enrolForm,
                                        program_id: v,
                                        mentor_id: enrolForm.mentor_id || next?.default_mentor_id || null,
                                        expected_end_date: expectedEndFromProgram(next, enrolForm.start_date),
                                    });
                                }}
                            />
                        </ModalField>
                        {selectedProgram && <ProgramPreview program={selectedProgram} />}
                        {selectedProgram && !(selectedProgram.phases || []).length && (
                            <p className="text-sm text-orange-600 dark:text-orange-400 rounded-lg border border-orange-200 dark:border-orange-900/40 bg-orange-50 dark:bg-orange-950/20 px-3 py-2">
                                This program has no phases yet. Add phases and trainings on Programs before enrolling.
                            </p>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <ModalField label="Intern" htmlFor="enrol-intern" required>
                                <Select
                                    id="enrol-intern"
                                    className={SELECT_CLASS}
                                    size="large"
                                    placeholder="Search employee"
                                    showSearch
                                    optionFilterProp="label"
                                    options={employees}
                                    value={enrolForm.employee_id || undefined}
                                    onChange={(v) => setEnrolForm({ ...enrolForm, employee_id: v })}
                                />
                            </ModalField>
                            <ModalField
                                label="Mentor"
                                htmlFor="enrol-mentor"
                                hint="Leave empty to use the program default."
                            >
                                <Select
                                    id="enrol-mentor"
                                    className={SELECT_CLASS}
                                    size="large"
                                    placeholder="Select mentor (optional)"
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                    options={employees}
                                    value={enrolForm.mentor_id || undefined}
                                    onChange={(v) => setEnrolForm({ ...enrolForm, mentor_id: v || null })}
                                />
                            </ModalField>
                        </div>
                        {alreadyEnrolled && (
                            <p className="text-sm text-orange-600 dark:text-orange-400 rounded-lg border border-orange-200 dark:border-orange-900/40 bg-orange-50 dark:bg-orange-950/20 px-3 py-2">
                                This intern is already active on this program.
                            </p>
                        )}
                        {enrolForm.mentor_id && enrolForm.mentor_id === enrolForm.employee_id && (
                            <p className="text-sm text-orange-600 dark:text-orange-400 rounded-lg border border-orange-200 dark:border-orange-900/40 bg-orange-50 dark:bg-orange-950/20 px-3 py-2">
                                An intern cannot be their own mentor.
                            </p>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <ModalField label="Start date" htmlFor="enrol-start" required>
                                <DatePicker
                                    id="enrol-start"
                                    className={PICKER_CLASS}
                                    size="large"
                                    value={enrolForm.start_date}
                                    onChange={(d) => setEnrolForm({
                                        ...enrolForm,
                                        start_date: d,
                                        expected_end_date: expectedEndFromProgram(selectedProgram, d),
                                    })}
                                    format="DD-MM-YYYY"
                                />
                            </ModalField>
                            <ModalField
                                label="Expected end"
                                htmlFor="enrol-end"
                                hint={selectedProgram?.duration_weeks
                                    ? `Filled from the program duration (${selectedProgram.duration_weeks} weeks).`
                                    : "Optional. Set a program duration to fill this automatically."}
                            >
                                <DatePicker
                                    id="enrol-end"
                                    className={PICKER_CLASS}
                                    size="large"
                                    value={enrolForm.expected_end_date}
                                    onChange={(d) => setEnrolForm({ ...enrolForm, expected_end_date: d })}
                                    format="DD-MM-YYYY"
                                />
                            </ModalField>
                        </div>
                        {selectedProgram && programTrainingCount(selectedProgram) > 0 && (
                            <p className="text-xs text-muted-foreground">
                                {programTrainingCount(selectedProgram)} training
                                {programTrainingCount(selectedProgram) === 1 ? "" : "s"} will be assigned on save.
                            </p>
                        )}
                    </form>
                )}
            </DialogModal>

            <DialogModal
                open={!!signOff}
                onClose={() => setSignOff(null)}
                title="Sign off phase"
                onSave={submitSignOff}
                saveLabel="Sign Off"
                closeLabel="Cancel"
                loading={saving}
                width="600px"
            >
                {signOff && (
                    <form className="space-y-4">
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                            {signOff.enrolment.employee_name}
                            <span className="text-muted-foreground"> · {signOff.enrolment.program_name}</span>
                        </p>
                        <ModalField label="Phase" required>
                            <Select
                                className={SELECT_CLASS}
                                size="large"
                                options={signOffPhaseOptions}
                                value={signOff.progress.id}
                                onChange={(id) => {
                                    const next = (signOff.enrolment.phase_progress || []).find((p) => p.id === id);
                                    if (next) setSignOff({ ...signOff, progress: next, rating: null, remarks: "" });
                                }}
                            />
                        </ModalField>
                        <div className="flex items-center gap-2">
                            <PhaseStatusPill value={signOff.progress.status} />
                            <span className="text-xs text-muted-foreground">
                                {signOff.progress.trainings_total > 0
                                    ? `${signOff.progress.trainings_completed} of ${signOff.progress.trainings_total} trainings complete`
                                    : "No trainings in this phase"}
                            </span>
                        </div>
                        {signOff.progress.trainings_outstanding > 0 && (
                            <p className="text-sm text-orange-600 dark:text-orange-400 rounded-lg border border-orange-200 dark:border-orange-900/40 bg-orange-50 dark:bg-orange-950/20 px-3 py-2">
                                {signOff.progress.trainings_outstanding} training(s) are still outstanding. Sign off only if you have assessed the intern directly, and record why below.
                            </p>
                        )}
                        <ModalField label="Rating">
                            <Select
                                className={SELECT_CLASS}
                                size="large"
                                placeholder="Rating (optional)"
                                allowClear
                                value={signOff.rating || undefined}
                                onChange={(v) => setSignOff({ ...signOff, rating: v || null })}
                                options={[1, 2, 3, 4, 5].map((n) => ({ value: n, label: `${n} / 5` }))}
                            />
                        </ModalField>
                        <ModalField
                            label="Remarks"
                            required={signOff.progress.trainings_outstanding > 0}
                            hint={signOff.progress.trainings_outstanding > 0
                                ? "Required while trainings are outstanding."
                                : "Optional notes for the intern record."}
                        >
                            <Textarea
                                rows={3}
                                placeholder={signOff.progress.trainings_outstanding > 0
                                    ? "Why this phase is signed off with outstanding trainings"
                                    : "Remarks (optional)"}
                                value={signOff.remarks}
                                onChange={(e) => setSignOff({ ...signOff, remarks: e.target.value })}
                                className={modalTextareaClass}
                            />
                        </ModalField>
                    </form>
                )}
            </DialogModal>

            <DialogModal
                open={!!detail}
                onClose={() => setDetail(null)}
                title={detail ? `${detail.employee_name} · ${detail.program_name}` : "Enrolment"}
                showActions={false}
                width="640px"
            >
                {detail && (
                    <div className="space-y-4">
                        <div className="flex flex-wrap items-center gap-2">
                            <EnrolmentStatusPill value={detail.status} />
                            <span className="text-xs text-muted-foreground">
                                {detail.progress_percent ?? 0}% complete
                                {detail.mentor_name ? ` · Mentor: ${detail.mentor_name}` : " · No mentor"}
                            </span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {dayjs(detail.start_date).format("DD-MM-YYYY")}
                            {detail.expected_end_date ? ` – ${dayjs(detail.expected_end_date).format("DD-MM-YYYY")}` : ""}
                            {detail.current_phase ? ` · Current: ${detail.current_phase}` : ""}
                        </p>
                        <ol className="space-y-2">
                            {(detail.phase_progress || []).map((p) => (
                                <li key={p.id} className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm font-medium">{p.sequence}. {p.phase_name}</span>
                                        <PhaseStatusPill value={p.status} />
                                        {p.rating ? <span className="text-xs text-muted-foreground">rated {p.rating}/5</span> : null}
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {p.trainings_total > 0
                                            ? `${p.trainings_completed}/${p.trainings_total} trainings complete`
                                            : "No trainings in this phase"}
                                        {p.evaluated_by ? ` · signed off by ${p.evaluated_by}` : ""}
                                    </p>
                                    {p.remarks ? <p className="text-xs italic mt-1 text-muted-foreground">&ldquo;{p.remarks}&rdquo;</p> : null}
                                </li>
                            ))}
                        </ol>
                        {detail.status === "completed" && (
                            <Button
                                size="sm"
                                className="bg-sidebar text-primary-foreground hover:bg-sidebar-primary"
                                onClick={() => getPrintData(INTERNSHIP_CERTIFICATE, { enrolment_id: detail.id })}
                            >
                                <Award className="h-4 w-4 mr-1" /> Certificate
                            </Button>
                        )}
                    </div>
                )}
            </DialogModal>

            <DialogModal
                open={!!withdrawTarget}
                onClose={() => setWithdrawTarget(null)}
                title="Withdraw intern"
                onSave={confirmWithdraw}
                saveLabel="Withdraw"
                closeLabel="Cancel"
                saveButtonColor="destructive"
                loading={saving}
                width="480px"
            >
                {withdrawTarget && (
                    <form className="space-y-4">
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                            Withdraw <span className="font-medium">{withdrawTarget.employee_name}</span> from{" "}
                            <span className="font-medium">{withdrawTarget.program_name}</span>.
                        </p>
                        <ModalField
                            label="Reason"
                            required
                            hint="Assigned trainings are kept so the intern can still complete them as an employee."
                        >
                            <Textarea
                                rows={3}
                                placeholder="Reason for withdrawal"
                                value={withdrawReason}
                                onChange={(e) => setWithdrawReason(e.target.value)}
                                className={modalTextareaClass}
                            />
                        </ModalField>
                    </form>
                )}
            </DialogModal>
        </ContainerCard>
    );
};

export default InternshipEnrolments;
